import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import LoginModel from '../../../../../models/user.model';
import { AppCommon } from '../../../../../providers/app-common';
import { SourcingNeedV2ViewModel } from '../../../../../models-views/sourcing-needs-v2.view.model';
import { StandardResponseModel } from '../../../../../models/project.model';

@Component({
  selector: 'app-sn-form-experience-v2',
  templateUrl: './sn-form-experience-v2.component.html',
  styleUrls: ['./sn-form-experience-v2.component.scss'],
  standalone: false
})
export class SnFormExperienceV2Component implements OnInit, OnChanges {
  @Input() eventId: number | null = null;
  @Input() data: any;
  @Output() closeModal = new EventEmitter<'success' | 'cancel'>();
  
  form!: FormGroup;
  isEdit: boolean = true;
  isView: boolean = false;
  existingData: any = null;
  existingRecordId: number | null = null;

  experienceTypesResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  experienceCategoryResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  experienceTestingTypeResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  
  isTastingExperience: boolean = false;
  private readonly TASTING_MODE = 'tasting';
  private readonly NON_TASTING_MODE = 'non_tasting';

  constructor(
    private fb: FormBuilder, 
    private vm: SourcingNeedV2ViewModel,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.vm.parentSnFormExperienceV2Component = this;
    this.initForm();
    this.vm.subscribeToExperienceTypes(
      this.experienceTypesResponseModel,
      this.experienceCategoryResponseModel,
      this.experienceTestingTypeResponseModel
    );
    this.vm.getExperienceTypes();
    
    this.setupFormListeners();
    
    setTimeout(() => {
      this.handleInputData();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.handleInputData();
    }
  }

  handleInputData(): void {
    if (this.data) {
      this.existingData = this.data;
      this.existingRecordId = this.data.id || null;
      this.isView = false;
      this.isEdit = true;

      setTimeout(() => {
        const mappedData = this.mapApiToForm(this.data);
        this.form.patchValue(mappedData, { emitEvent: false });

        this.applyExperienceMode(mappedData.experience_mode, true);

        if (mappedData.experience_type) {
          this.vm.getExperienceCategory(mappedData.experience_type);
        }

        if (mappedData.experience_mode === this.TASTING_MODE && mappedData.experience_category) {
          this.vm.getExperienceTestingType(mappedData.experience_category);
        }

        this.updateTestingTypeValidators(mappedData.experience_mode, mappedData.experience_category);
        this.cdr.detectChanges();
      }, 300);
    } else {
      this.existingData = null;
      this.existingRecordId = null;
      this.isView = false;
      this.isEdit = true;
      this.form.reset();
      this.form.patchValue({ experience_mode: this.NON_TASTING_MODE }, { emitEvent: false });
      this.isTastingExperience = false;
      this.form.enable();
      this.cdr.detectChanges();
    }
  }

  mapApiToForm(data: any): any {
    // Map existing data if editing
    if (data) {
      const isTasting = this.isTastingFromData(data);
      return {
        experience_mode: isTasting ? this.TASTING_MODE : this.NON_TASTING_MODE,
        experience_type: data.experience_type || null,
        experience_category: data.experience_category || null,
        experience_testing_type: data.experience_testing_type || null
      };
    }
    return {
      experience_mode: this.NON_TASTING_MODE,
      experience_type: null,
      experience_category: null,
      experience_testing_type: null
    };
  }

  initForm(): void {
    this.form = this.fb.group({
      experience_mode: [this.NON_TASTING_MODE, Validators.required],
      experience_type: [null, Validators.required],
      experience_category: [null, Validators.required],
      experience_testing_type: [null],
    });
  }

  setupFormListeners(): void {
    this.form.get('experience_mode')?.valueChanges.subscribe((mode) => {
      this.applyExperienceMode(mode, false);
    });

    // Experience Type → Experience Category
    this.form.get('experience_type')?.valueChanges.subscribe(typeId => {
      this.form.patchValue({
        experience_category: null,
        experience_testing_type: null
      }, { emitEvent: false });
      this.experienceCategoryResponseModel.next(new StandardResponseModel());
      this.experienceTestingTypeResponseModel.next(new StandardResponseModel());
      
      if (typeId) {
        this.vm.getExperienceCategory(typeId);
      }

      const mode = this.form.get('experience_mode')?.value;
      this.isTastingExperience = mode === this.TASTING_MODE;
      this.updateTestingTypeValidators(mode, null);
    });

    // Experience Category → Experience Testing Type (conditional)
    this.form.get('experience_category')?.valueChanges.subscribe(categoryId => {
      this.form.patchValue({ experience_testing_type: null }, { emitEvent: false });
      this.experienceTestingTypeResponseModel.next(new StandardResponseModel());
      const mode = this.form.get('experience_mode')?.value;
      this.isTastingExperience = mode === this.TASTING_MODE;
      
      if (categoryId) {
        if (mode === this.TASTING_MODE) {
          this.vm.getExperienceTestingType(categoryId);
        }
      }

      this.updateTestingTypeValidators(mode, categoryId);
    });
  }

  onSave(): void {
    if (this.form.valid) {
      const loggedInUser = AppCommon.getloggedInObject();
      const userId = loggedInUser?.user?.id;
      
      const payload: any = {
        sourcing_need: this.eventId,
        experience_type: this.form.value.experience_type,
        experience_category: this.form.value.experience_category,
      };
      
      // Only include experience_testing_type if it's set and valid
      if (this.isTastingExperience && this.form.value.experience_testing_type) {
        payload.experience_testing_type = this.form.value.experience_testing_type;
      }
      
      if (this.existingRecordId) {
        // Update existing record
        payload.modified_by = userId;
        this.vm.updateSourcingNeedExperienceSelection(payload, this.existingRecordId, 'sn-form-experience-v2');
      } else {
        // Create new record
        payload.created_by = userId;
        this.vm.createSourcingNeedExperienceSelection(payload, 'sn-form-experience-v2');
      }
    }
  }

  experienceAdded() {
    this.closeModal.emit('success');
    
    // Refresh parent component data
    if (this.eventId) {
      this.vm.getSouringNeedbyId(this.eventId, 'cet-sourcing-needs-v2');
    }
  }

  onEdit(): void {
    this.isEdit = true;
    this.isView = false;
    this.form.enable();
  }

  onCancel(): void {
    this.closeModal.emit('cancel');
  }

  get filteredExperienceTypes(): any[] {
    const tastingId = this.getTastingTypeId();
    return (this.experienceTypesResponseModel.value.results || []).filter((type: any) => type.id !== tastingId);
  }

  private applyExperienceMode(mode: string, preserveSelection: boolean): void {
    const isTastingMode = mode === this.TASTING_MODE;
    const tastingTypeId = this.getTastingTypeId();

    this.isTastingExperience = isTastingMode;

    if (!preserveSelection) {
      this.form.patchValue({
        experience_category: null,
        experience_testing_type: null
      }, { emitEvent: false });
      this.experienceCategoryResponseModel.next(new StandardResponseModel());
      this.experienceTestingTypeResponseModel.next(new StandardResponseModel());
    }

    if (isTastingMode) {
      if (tastingTypeId) {
        this.form.get('experience_type')?.setValue(tastingTypeId, { emitEvent: !preserveSelection });
      }
    } else {
      if (this.form.get('experience_type')?.value === tastingTypeId) {
        this.form.get('experience_type')?.setValue(null, { emitEvent: false });
      }
      this.updateTestingTypeValidators(mode, null);
    }
  }

  private updateTestingTypeValidators(mode: string, categoryId: number | null): void {
    const experienceTestingControl = this.form.get('experience_testing_type');
    if (!experienceTestingControl) {
      return;
    }

    if (mode === this.TASTING_MODE && !!categoryId) {
      experienceTestingControl.setValidators(Validators.required);
    } else {
      experienceTestingControl.clearValidators();
    }

    experienceTestingControl.updateValueAndValidity({ emitEvent: false });
  }

  private getTastingTypeId(): number | null {
    const tastingOption = (this.experienceTypesResponseModel.value.results || []).find(
      (type: any) => this.isTastingTypeByName(type?.name)
    );

    return tastingOption?.id ?? null;
  }

  private isTastingTypeByName(name: string): boolean {
    return (name || '').trim().toLowerCase() === 'tasting experience';
  }

  private isTastingTypeById(typeId: number | null): boolean {
    if (!typeId) {
      return false;
    }

    const matchedType = (this.experienceTypesResponseModel.value.results || []).find(
      (type: any) => type.id === typeId
    );

    return this.isTastingTypeByName(matchedType?.name);
  }

  private isTastingFromData(data: any): boolean {
    const typeName = data?.experience_type_details?.name;
    if (this.isTastingTypeByName(typeName)) {
      return true;
    }

    return this.isTastingTypeById(data?.experience_type ?? null);
  }
}